import { createContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { registerEscalationPush, unregisterEscalationPush } from "../utils/pushNotifications";

export const AuthContext = createContext();

// ─── Tuning constants ─────────────────────────────────────────────────────
const TOKEN_CHECK_INTERVAL_MS  = 5 * 60 * 1000;  // 5 minutes
const UPDATE_CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const CHECK_COOLDOWN_MS        = 30 * 1000;       // 30 seconds
const API_TIMEOUT_MS           = 30000;           // 30 seconds (increased from 8s)
const INITIAL_CHECK_DELAY_MS   = 2000;            // 2 seconds

export const AuthProvider = ({ children }) => {
  const [user, setUser]                   = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [forceLogout, setForceLogout]     = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [sessionId, setSessionId]         = useState(null);
  const [redirectPath, setRedirectPath]   = useState(null);

  const navigate          = useNavigate();
  const location          = useLocation();

  const checkIntervalRef  = useRef(null);
  const updateIntervalRef = useRef(null);
  const isCheckingRef     = useRef(false);
  const lastCheckTimeRef  = useRef(0);
  const pathnameRef       = useRef(location.pathname);
  const initialCheckDone  = useRef(false);
  const abortControllerRef = useRef(null);

  useEffect(() => { pathnameRef.current = location.pathname; }, [location.pathname]);

  useEffect(() => {
    if (location.state?.from) {
      setRedirectPath(location.state.from);
    }
    const queryParams = new URLSearchParams(location.search);
    const redirectParam = queryParams.get('redirect');
    if (redirectParam) {
      setRedirectPath(redirectParam);
    }
  }, [location]);

  const API_BASE_URL = "https://api.miplapp.in/public/api";

  const initializeMessageCount = useCallback(() => {
    if (typeof window !== "undefined" && !window.updateMessageCount) {
      window.updateMessageCount = () => {
        window.dispatchEvent(new Event("messageCountUpdate"));
      };
    }
  }, []);

  const clearAuthData = useCallback(() => {
    const keys = ["token", "user", "session_id", "lastTokenCheck", "lastUpdateCheck", "superadmin_view_target", "superadmin_dashboard_target"];
    keys.forEach(k => { 
      localStorage.removeItem(k); 
      sessionStorage.removeItem(k); 
    });
    setUser(null);
    setSessionId(null);
    setForceLogout(false);
    setPasswordChanged(false);
    setRedirectPath(null);
    
    if (checkIntervalRef.current) { 
      clearInterval(checkIntervalRef.current);  
      checkIntervalRef.current = null; 
    }
    if (updateIntervalRef.current) { 
      clearInterval(updateIntervalRef.current); 
      updateIntervalRef.current = null; 
    }
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const logout = useCallback(async (options = { redirect: true, passwordChanged: false }) => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (token) {
        // Remove this device's push subscription before clearing credentials
        unregisterEscalationPush(token).catch(() => { /* silent fail */ });

        await axios.post(`${API_BASE_URL}/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: API_TIMEOUT_MS
        }).catch(() => { /* silent fail */ });
      }
    } catch { /* ignore */ } finally {
      clearAuthData();
      if (options.passwordChanged) setPasswordChanged(true);
      if (options.redirect !== false) navigate("/login", { replace: true });
    }
  }, [navigate, clearAuthData]);

  const checkTokenValidity = useCallback(async (token) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    
    try {
      const response = await axios.get(`${API_BASE_URL}/check-token`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: API_TIMEOUT_MS,
        signal: abortControllerRef.current.signal
      });
      
      return {
        valid:           response.data.valid !== false,
        forceLogout:     response.data.force_logout     || false,
        passwordChanged: response.data.password_changed || false,
        user:            response.data.user,
        latest_version:  response.data.latest_version
      };
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log("Token check cancelled");
        return { valid: true };
      }
      
      if (err.response?.status === 401) {
        const data = err.response.data;
        return {
          valid:           false,
          forceLogout:     data.force_logout     || false,
          passwordChanged: data.password_changed || false
        };
      }
      
      // 429 rate limit or network error → assume still valid
      if (err.response?.status === 429 || err.code === 'ECONNABORTED' || !err.response) {
        console.warn("Token check skipped (rate limit / timeout) — keeping session.");
        return { valid: true };
      }
      
      console.error("Token check error:", err.message);
      return { valid: true };
    }
  }, []);

  const checkForUpdates = useCallback(async (token) => {
    try {
      const lastCheck = localStorage.getItem("lastUpdateCheck") || "0";
      const response = await axios.get(`${API_BASE_URL}/user/updates`, {
        headers: { Authorization: `Bearer ${token}` },
        params:  { lastCheck },
        timeout: API_TIMEOUT_MS
      });
      if (response.data.updated) {
        window.dispatchEvent(new CustomEvent("userDataUpdated", {
          detail: { changes: response.data.changes }
        }));
        return true;
      }
      return false;
    } catch (err) {
      return false;
    }
  }, []);

  const startPeriodicChecks = useCallback((token) => {
    if (checkIntervalRef.current)  clearInterval(checkIntervalRef.current);
    if (updateIntervalRef.current) clearInterval(updateIntervalRef.current);

    checkIntervalRef.current = setInterval(async () => {
      try {
        const result = await checkTokenValidity(token);
        if (!result.valid && result.forceLogout) {
          clearInterval(checkIntervalRef.current);
          clearInterval(updateIntervalRef.current);
          await logout({ redirect: false, passwordChanged: result.passwordChanged });
          setForceLogout(true);
          setPasswordChanged(result.passwordChanged || false);
        }
      } catch { /* silent */ }
    }, TOKEN_CHECK_INTERVAL_MS);

    updateIntervalRef.current = setInterval(async () => {
      try {
        await checkForUpdates(token);
        localStorage.setItem("lastUpdateCheck", Date.now().toString());
      } catch { /* silent */ }
    }, UPDATE_CHECK_INTERVAL_MS);
  }, [checkTokenValidity, checkForUpdates, logout]);

  const authAxios = useMemo(() => {
    const instance = axios.create({ baseURL: API_BASE_URL, timeout: API_TIMEOUT_MS });

    instance.interceptors.request.use(config => {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    instance.interceptors.response.use(
      response => response,
      async err => {
        if (err.response?.status === 401) {
          const data = err.response.data;
          if (data?.force_logout || data?.password_changed) {
            await logout({ redirect: false, passwordChanged: data.password_changed });
            setForceLogout(true);
            setPasswordChanged(data.password_changed || false);
            window.dispatchEvent(new CustomEvent("forceLogout", {
              detail: { 
                message: data.message || "Your session has expired", 
                passwordChanged: data.password_changed 
              }
            }));
          } else {
            await logout({ redirect: false });
          }
        }
        if (err.response?.status !== 429) {
          return Promise.reject(err);
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [logout]);

  const fetchUserDetails = useCallback(async (userId) => {
    try {
      const res = await authAxios.get(`/users/${userId}`);
      return res.data || {};
    } catch {
      return {};
    }
  }, [authAxios]);

  const checkAuth = useCallback(async (options = {}) => {
    const now = Date.now();

    if (!options.force && isCheckingRef.current) return;
    if (!options.force && (now - lastCheckTimeRef.current < CHECK_COOLDOWN_MS)) return;

    isCheckingRef.current = true;
    lastCheckTimeRef.current = now;

    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) {
      setLoading(false);
      isCheckingRef.current = false;
      const publicPaths = ["/login", "/forgot-password", "/reset-password"];
      if (!publicPaths.includes(pathnameRef.current)) {
        navigate("/login", { replace: true });
      }
      return;
    }

    try {
      const decoded = jwtDecode(token);

      if (decoded.exp * 1000 < Date.now()) throw new Error("Token expired");
      if (decoded.status === "Inactive")   throw new Error("User account is inactive");

      const validation = await checkTokenValidity(token);
      
      if (!validation.valid) {
        if (validation.forceLogout) {
          await logout({ redirect: false, passwordChanged: validation.passwordChanged });
          setForceLogout(true);
          setPasswordChanged(validation.passwordChanged || false);
          setLoading(false);
          isCheckingRef.current = false;
          return;
        }
        throw new Error("Token invalid");
      }

      const userData = {
        id:                  decoded.id,
        name:                decoded.name  || "User",
        email:               decoded.email,
        mobile:              decoded.mobile,
        patient_id:          decoded.patient_id || null,
        role:                decoded.role  || "user",
        status:              decoded.status || "Active",
        passwordVersion:     decoded.pwdv  || 1,
        lastPasswordChange:  decoded.pwdch || null,
        sessionId:           decoded.sess  || null,
      };

      setSessionId(decoded.sess);
      localStorage.setItem("session_id", decoded.sess || "");

      if (userData.role === "admin") {
        const extra = await fetchUserDetails(decoded.id);
        Object.assign(userData, extra);
      }

      setUser(userData);
      const storage = localStorage.getItem("token") ? localStorage : sessionStorage;
      storage.setItem("user", JSON.stringify(userData));

      initializeMessageCount();
      
      if (!checkIntervalRef.current && !updateIntervalRef.current) {
        startPeriodicChecks(token);
      }

      if (redirectPath) {
        const path = redirectPath;
        setRedirectPath(null);
        navigate(path, { replace: true });
        setLoading(false);
        isCheckingRef.current = false;
        return;
      }

      if (["/login", "/forgot-password"].includes(pathnameRef.current)) {
        const dashboardPath = userData.role === "admin" ? "/admin-dashboard" : 
                             userData.role === "doctor" ? "/doctor-dashboard" : "/";
        navigate(dashboardPath, { replace: true });
      }
    } catch (err) {
      console.error("Authentication error:", err.message);
      setError(err.message);
      await logout({ redirect: false });
    } finally {
      setLoading(false);
      isCheckingRef.current = false;
    }
  }, [
    navigate, fetchUserDetails, logout, initializeMessageCount,
    checkTokenValidity, startPeriodicChecks, redirectPath
  ]);

  const login = useCallback(async (token, rememberMe = true, sid = null) => {
    try {
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem("token", token);
      storage.setItem("lastTokenCheck", Date.now().toString());
      if (sid) { 
        storage.setItem("session_id", sid); 
        setSessionId(sid); 
      }

      const decoded   = jwtDecode(token);
      const tempUser  = {
        id:               decoded.id,
        name:             decoded.name  || "User",
        email:            decoded.email,
        mobile:           decoded.mobile,
        patient_id:       decoded.patient_id || null,
        role:             decoded.role  || "user",
        status:           decoded.status || "Active",
        passwordVersion:  decoded.pwdv  || 1,
        lastPasswordChange: decoded.pwdch || null,
        sessionId:        decoded.sess  || null,
      };

      setUser(tempUser);
      storage.setItem("user", JSON.stringify(tempUser));
      initializeMessageCount();
      startPeriodicChecks(token);

      // Register for Web Push after login so the server can target this
      // specific user for escalation notifications.
      registerEscalationPush(token).catch((e) =>
        console.warn("Push registration skipped:", e)
      );

      await checkAuth({ force: true });

      if (typeof window.updateMessageCount === "function") {
        window.updateMessageCount();
      }
    } catch (err) {
      console.error("Login processing error:", err);
      await logout({ redirect: false });
      throw err;
    }
  }, [checkAuth, logout, initializeMessageCount, startPeriodicChecks]);

  useEffect(() => {
    const handler = () => checkAuth({ force: true });
    window.addEventListener("userDataUpdated", handler);
    return () => window.removeEventListener("userDataUpdated", handler);
  }, [checkAuth]);

  useEffect(() => {
    if (initialCheckDone.current) return;
    
    const timer = setTimeout(() => {
      initialCheckDone.current = true;
      checkAuth({ force: true });
    }, INITIAL_CHECK_DELAY_MS);

    const handleStorage = (e) => { 
      if (e.key === "token") {
        setTimeout(() => checkAuth({ force: true }), 100);
      }
    };
    
    const handleForce = (e) => { 
      if (e.detail?.passwordChanged) setPasswordChanged(true); 
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("forceLogout", handleForce);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("forceLogout", handleForce);
      if (checkIntervalRef.current)  clearInterval(checkIntervalRef.current);
      if (updateIntervalRef.current) clearInterval(updateIntervalRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    forceLogout,
    passwordChanged,
    sessionId,
    authAxios,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    refreshAuth: () => checkAuth({ force: true }),
    setRedirectPath,
  }), [user, loading, error, forceLogout, passwordChanged, sessionId, authAxios, login, logout, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;